import React, { ComponentType } from 'react';

import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { Main } from '@atlaskit/navigation-system/layout/main';
import Box from '@atlaskit/primitives/box';
import { media } from '@atlaskit/primitives/responsive/media';
// eslint-disable-next-line @townsquare/rules/no-xcss
import { xcss } from '@atlaskit/primitives/xcss';
import {
	useGlobalObjectPreviewPanels,
	usePreviewPanelIFrameCommunication,
} from '@atlassian/preview-panel-global-objects/global-object-preview-panels';

import { useRovoWorkspace } from '@townsquare/collab-context';
import { useIsForYouRefreshed } from '@townsquare/for-you-refresh';
import { GriffinMainGrid } from '@townsquare/griffin-main-grid';
import { GriffinMainGridContent } from '@townsquare/griffin-main-grid/content';
import { GriffinMainGridHeader } from '@townsquare/griffin-main-grid/header';
import { CustomWidthPreviewPanelContainer } from '@townsquare/preview-panels/customWidthPreviewPanelContainer';
import { AggRelayEnvironmentProvider } from '@townsquare/relay-utils/agg-environment';
import { RouteComponent } from '@townsquare/router/RouteComponent';
import { HomepageRoute, LayoutType } from '@townsquare/router/route-types';
import { useRouter } from '@townsquare/router/useRouter';
import { sharedConversationPreviewRoute } from '@townsquare/routes/shared-conversation-preview';
import { RovoButton } from '@townsquare/rovo-ai-button';
import { useRightSidebarState } from '@townsquare/sidebar-panel/right-sidebar';
import { isEntryPointPanelSupported } from '@townsquare/sidebar-panel/utils';
import { LegacySidebarPanel } from '@townsquare/sidebar-panel-utils/legacy-sidebar-panel';
import { fg } from '@townsquare/stat-sig/gate';

import { useIsOnFullPageRovoChat } from '../utils/route-utils';

import { GriffinResponsiveAside } from './GriffinResponsiveAside';

// Expected with for main column CONTENT is 1232px.
const MAIN_CONTENT_LX_WIDTH = '1232px';

type AsideType = Exclude<HomepageRoute['aside'], undefined>;

type GriffinLayoutProps = {
	aside: AsideType | undefined;
	layout: LayoutType;
	isScrollContained?: boolean;
	includeGridBackground?: boolean;
};

/**
 * Additional default styling to conform to a grid structure
 */
const defaultMainContentStyles = xcss({
	position: 'relative',
	marginInlineEnd: 'auto',
	marginInlineStart: 'auto',
	// We want the column content width to be the set width, with padding and margin added on top.
	boxSizing: 'content-box',
	maxWidth: MAIN_CONTENT_LX_WIDTH,
	marginBlock: 'space.400',
	paddingInline: 'space.200',
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles
	[media.above.md]: {
		paddingInline: 'space.400',
	},
});

const gridLineColor = `#88888f0d`;

const gridStyles = xcss({
	backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px),
		linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
	backgroundSize: '24px 24px',
	backgroundRepeat: 'repeat',
	backgroundPositionX: '-1px',
	backgroundPositionY: '-1px',
	minHeight: 'calc(100vh - 56px)',
	height: 'fit-content',
});

const refreshedWidthCapStyles = xcss({
	maxWidth: '1000px',
});

const mainContentWithAsideStyles = xcss({
	// If there is an aside column, we don't want the max page width to exceed 800px.
	maxWidth: '800px',
});

const GriffinMainContent = (props: GriffinLayoutProps) => {
	const useDefaultStyle = props.layout === 'home';
	const isForYouRefreshed = useIsForYouRefreshed();

	const page = (
		<GriffinMainGridContent isScrollContained={props.isScrollContained}>
			{useDefaultStyle ? (
				<Box
					xcss={[
						defaultMainContentStyles,
						props.aside ? mainContentWithAsideStyles : undefined,
						useDefaultStyle && isForYouRefreshed && props.includeGridBackground
							? refreshedWidthCapStyles
							: undefined,
					]}
				>
					<RouteComponent />
				</Box>
			) : (
				<RouteComponent />
			)}
		</GriffinMainGridContent>
	);

	if (isForYouRefreshed && props.includeGridBackground) {
		return <Box xcss={gridStyles}>{page}</Box>;
	}

	return page;
};

const GriffinWithAside = (
	props: GriffinLayoutProps & { aside: AsideType; hasNewHeader: boolean },
) => {
	const AsideComponent = props.aside;

	return (
		<>
			<GriffinMainContent {...props} />
			<GriffinResponsiveAside hasNewHeader={props.hasNewHeader}>
				<AsideComponent />
			</GriffinResponsiveAside>
		</>
	);
};

const AggRelayProvider = ({ children }: { children: React.ReactNode }) => (
	<AggRelayEnvironmentProvider>{children}</AggRelayEnvironmentProvider>
);

export const GriffinLayout = ({
	PlatformSidebarPanel,
}: {
	PlatformSidebarPanel: ComponentType;
}): React.JSX.Element => {
	const { currentPanel } = useRightSidebarState();
	const [routerState] = useRouter();
	const cloudId = useRovoWorkspace();
	const { openPreviewPanel } = useGlobalObjectPreviewPanels('home', cloudId ?? '');
	usePreviewPanelIFrameCommunication(openPreviewPanel);
	const isForYouRefreshed = useIsForYouRefreshed();

	const route = routerState.route as HomepageRoute;
	const layout = route.nav4Layout ?? (route?.layout || 'home');

	const HeaderComponent = route.header;
	const AggContainer = route.aggContainer;

	const isOnFullPageRovoChat = useIsOnFullPageRovoChat();
	const shouldContainFullPageChatScroll =
		isOnFullPageRovoChat && fg('townsquare_rovo_chat_contained_scroll');

	const shouldShowRovoButton = (routeName: string) => {
		if (routeName === 'hometab') {
			// must not be on home tab page to show the button
			return false;
		}

		if (routeName === sharedConversationPreviewRoute.name) {
			return false;
		}

		return !isOnFullPageRovoChat;
	};

	const showRovoButton = shouldShowRovoButton(routerState.route.name);
	const shouldShowSidebarPanel = currentPanel && isEntryPointPanelSupported(currentPanel);
	const includeGridBackground = isForYouRefreshed && routerState.route.name === 'hometab';

	const content = route.aside ? (
		<GriffinWithAside
			layout={layout}
			aside={route?.aside}
			hasNewHeader={!!route.header}
			isScrollContained={shouldContainFullPageChatScroll}
			includeGridBackground={includeGridBackground}
		/>
	) : (
		<GriffinMainContent
			layout={layout}
			aside={route?.aside}
			isScrollContained={shouldContainFullPageChatScroll}
			includeGridBackground={includeGridBackground}
		/>
	);

	const shouldShowEntryPointSidebarPanel = !isOnFullPageRovoChat && shouldShowSidebarPanel;

	const entryPointSidebarPanel = shouldShowEntryPointSidebarPanel ? (
		<PlatformSidebarPanel key="entry-point-sidebar-panel" />
	) : null;

	// `ExitingPersistence` has to sit outside the conditional so the panel it is holding onto
	// survives `shouldShowEntryPointSidebarPanel` flipping to false and can play its exit motion.
	const SidebarPanel = (
		<>
			{fg('platform-dst-motion-uplift-panel') ? (
				<ExitingPersistence>{entryPointSidebarPanel}</ExitingPersistence>
			) : (
				entryPointSidebarPanel
			)}
			{!isOnFullPageRovoChat && !shouldShowSidebarPanel && (
				<LegacySidebarPanel currentPanel={currentPanel} />
			)}
		</>
	);

	const RelayProvider = route.relayEnvironment === 'agg' ? AggRelayProvider : React.Fragment;

	if (layout === 'custom') {
		return (
			<RelayProvider>
				<Main testId={routerState.route.name}>
					<RouteComponent />
					{showRovoButton && <RovoButton />}
				</Main>
				{SidebarPanel}
				<CustomWidthPreviewPanelContainer />
			</RelayProvider>
		);
	}

	const LayoutContent = (
		<RelayProvider>
			<Main testId={routerState.route.name}>
				<GriffinMainGrid isSinglePaneScrollContainer={shouldContainFullPageChatScroll}>
					{HeaderComponent && (
						<GriffinMainGridHeader>
							<HeaderComponent />
						</GriffinMainGridHeader>
					)}
					{content}
					{showRovoButton && <RovoButton />}
				</GriffinMainGrid>
			</Main>
			{SidebarPanel}
			<CustomWidthPreviewPanelContainer />
		</RelayProvider>
	);

	if (fg('townsquare_enable_combined_agg_query_sidebar') && AggContainer) {
		return <AggContainer>{LayoutContent}</AggContainer>;
	}

	return LayoutContent;
};
